import React, {useContext, useState} from "react";
import {Stage} from "../../../api/model/scenario";
import {TravelStepComponent} from "../Step";
import {Response, saveQuotation} from "../../../api/quotationApiClient";
import {TravelQuotation, ITravelWebPerson} from "../../../model/quotation";
import {ErrorMessageDialog} from "../../dialogs/ErrorMessageDialog";
import {handleAfterStepErrors} from "../error-handlers";
import * as log from "../../../utils/log";
import {LogFunctionalModule} from "../../../utils/log";
import {ErrorDialogType} from "../../../utils/hooks/useErrorDialog";
import {TravelScenarioContext} from "../../../contexts/TravelScenatioContext";
import {TravelQuotationContext} from "../../../contexts/TravelQuotationContext";
import {ProcessingContext} from "../../../contexts/ProcessingContext";
import {ProductType} from "../../../api/model/common";

interface StageComponentProps {
    stage: Stage;
}

export const TravelStageComponent = ({stage}: StageComponentProps) => {
    const {currentStep, setCurrentStep, setPreviousStep, nextStageHandler} = useContext(TravelScenarioContext);
    const {setQuotation} = useContext(TravelQuotationContext);
    const {setProcessingOpen} = useContext(ProcessingContext);

    const [openErrorMessageDialog, setOpenErrorMessageDialog] = useState(false);
    const [textErrorMessageDialog, setTextErrorMessageDialog] = useState("");
    const [textDetailErrorMessageDialog, setTextDetailErrorMessageDialog] = useState("");

    const nextStepHandler = (quotation: TravelQuotation) => {
        updateQuotationWithMetadata(quotation);
        setProcessingOpen(true);

        saveQuotation(quotation, ProductType.TRAVEL)
            .then((res) => handleQuotationResponse(res, quotation));
    };

    const updateQuotationWithMetadata = (quotation: TravelQuotation) => {
        quotation.currentStep = currentStep.code;
        quotation.admitadId = getCookie("_aid");
        quotation.salesDoublerId = getCookie("sd_user");
    };

    const handleQuotationResponse = (response: Response, quotation: TravelQuotation) => {
        if (!response.success) {
            handleQuotationError(response, quotation);
            return;
        }
        updatePersonsInQuotation(response, quotation);
        setQuotation(response.data);
        setProcessingOpen(false);
        setPreviousStep(currentStep);
        navigateToNextStep(response.data);
        window.scrollTo(0, 0);
    };

    const updatePersonsInQuotation = (response: Response, quotation: TravelQuotation) => {
        response.data.travelWebContract.persons = quotation.travelWebContract.persons.map((person, idx) => {
            const personCompared: ITravelWebPerson = {
                ...person,
                id: response.data.travelWebContract.persons[idx].id
            }
            Object.keys(personCompared).forEach((key) => {
                if( key === 'id') return
                personCompared[key] = response.data.travelWebContract.persons[idx][key] || personCompared[key]
            })
            return personCompared
        })
    };

    const navigateToNextStep = (quotation: TravelQuotation) => {
        const currentStepIndex = stage.steps.indexOf(currentStep);
        if (stage.steps.length === currentStepIndex + 1) {
            nextStageHandler(quotation);
        } else {
            setCurrentStep(stage.steps[currentStepIndex + 1]);
        }
    };

    const handleQuotationError = (response: Response, quotation: TravelQuotation) => {
        handleAfterStepErrors(response, (response) => {
            setProcessingOpen(false);
            setTextErrorMessageDialog(response.errorResponse.text);
            setTextDetailErrorMessageDialog(response.errorResponse.detailText);
            setOpenErrorMessageDialog(true);
        });
        log.sendLog(new Date(), LogFunctionalModule.STORE_TRAVEL, 'ERROR', "SAVE_QUOTATION", [{"quotation": quotation}], response.errorResponse.text);
    };

    const getCookie = (name: string): string | undefined => {
        const regex = new RegExp(
            `(?:^|; )${name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1')}=([^;]*)`
        );
        const matches = regex.exec(document.cookie);
        return matches ? decodeURIComponent(matches[1]) : undefined;
    };

    const backStepHandler = (quotation: TravelQuotation, stepCallback?: (stage: Stage) => void) => {
        const currentStepIndex = stage.steps.indexOf(currentStep);
        setPreviousStep(currentStep);
        setCurrentStep(stage.steps[currentStepIndex - 1]);
        quotation.currentStep = currentStep.code;
        setQuotation(quotation);

        if (stepCallback) {
            stepCallback(stage);
        }

        window.scrollTo(0, 0);
    };

    return (
        <div className={'stage'}>
            <TravelStepComponent step={currentStep}
                           nextStepHandler={nextStepHandler}
                           backStepHandler={backStepHandler}/>

            <ErrorMessageDialog type={ErrorDialogType.ERROR}
                                text={textErrorMessageDialog}
                                open={openErrorMessageDialog}
                                onClickListener={() => setOpenErrorMessageDialog(false)}
                                textDetail={textDetailErrorMessageDialog}/>
        </div>
    )
};